import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  service: string;
  message: string;
}

const services = [
  'IT Managed Services',
  'Cybersecurity Solutions',
  'Cloud Computing',
  'IT Consulting',
  'IT Infrastructure Deployment',
  'Custom White-Label SaaS Platform',
  'Disaster Recovery & Business Continuity',
  'Other / General Inquiry',
];

const offices = [
  {
    city: 'Lagos (HQ)',
    address: 'Suite 30, District Plaza, Simbiat Abiola Way',
    state: 'Ikeja, Lagos, Nigeria',
    phone: '+234 8033019908',
  },
];

export default function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      });
      await fetch('https://readdy.ai/api/form/d7mtq4fu2vahpmebfcgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-6 bg-white border-b border-black/[0.06]">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <span className="eyebrow">Let&apos;s Talk</span>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-page-ink mb-3 tracking-[-0.03em]">Contact Us</h1>
          <p className="text-page-muted text-base max-w-2xl mx-auto leading-relaxed font-light">
            Ready to transform your IT? Our experts are standing by for a free consultation. No commitment, no pressure.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-8 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left - Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Contacts */}
            <div>
              <h2 className="text-2xl font-extrabold text-page-ink mb-6 tracking-[-0.02em]">Get In Touch</h2>
              <div className="space-y-4">
                {[
                  { icon: 'ri-phone-line', title: 'Call Us', value: '+234 8033019908', sub: 'Mon–Fri, 8am–6pm WAT' },
                  { icon: 'ri-mail-line', title: 'Email Us', value: 'info@sunstonesystem.com', sub: 'We reply within 2 hours' },
                ].map(({ icon, title, value, sub }) => (
                  <div key={title} className="flex items-start gap-4 p-5 bg-white rounded-xl border border-black/[0.08] hover:border-[#c97a2b]/30 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/20 rounded-xl flex-shrink-0">
                      <i className={`${icon} text-[#c97a2b] text-lg`} />
                    </div>
                    <div>
                      <div className="font-bold text-page-ink text-sm mb-0.5">{title}</div>
                      <div className="text-[#c97a2b] font-semibold text-sm">{value}</div>
                      <div className="text-page-faint text-xs mt-0.5">{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Offices */}
            <div>
              <h3 className="text-lg font-bold text-page-ink mb-4">Our Offices</h3>
              <div className="space-y-3">
                {offices.map((office) => (
                  <div key={office.city} className="p-5 border border-black/[0.08] bg-white rounded-xl hover:border-[#c97a2b]/25 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-2-fill text-[#c97a2b] text-sm" />
                      </div>
                      <span className="font-bold text-page-ink text-sm">{office.city}</span>
                    </div>
                    <p className="text-page-faint text-xs leading-relaxed ml-6">
                      {office.address}<br />{office.state}
                    </p>
                    <p className="text-[#c97a2b] text-xs mt-2 ml-6">{office.phone}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Response Promise */}
            <div className="bg-[#c97a2b] rounded-xl p-6 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                <i className="ri-timer-flash-line text-xl" />
              </div>
              <h4 className="font-bold text-lg mb-2">Our Response Promise</h4>
              <p className="text-white/80 text-sm leading-relaxed">
                Every inquiry receives a response from a human expert within <strong className="text-white">2 business days</strong>. No bots, no runaround.
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              {submitted ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#c97a2b]/10 border border-[#c97a2b]/25 rounded-full mx-auto mb-5">
                    <i className="ri-checkbox-circle-line text-[#c97a2b] text-3xl" />
                  </div>
                  <h3 className="text-2xl font-extrabold text-page-ink mb-3">Message Sent!</h3>
                  <p className="text-page-muted max-w-sm mx-auto text-sm">
                    Thank you for reaching out. One of our experts will be in touch within 2 business hours.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', company: '', phone: '', service: '', message: '' }); }}
                    className="mt-8 btn-primary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-extrabold text-page-ink mb-2 tracking-[-0.02em]">Send Us a Message</h2>
                  <p className="text-page-muted text-sm mb-8">Fill in the form below and we'll connect you with the right expert.</p>

                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-page-muted mb-1.5" htmlFor="contact-name">
                          Full Name <span className="text-[#c97a2b]">*</span>
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Smith"
                          className="w-full px-4 py-3 border border-black/[0.1] bg-page rounded-lg text-sm text-page-ink placeholder-page-faint focus:outline-none focus:border-[#c97a2b]/50 focus:ring-1 focus:ring-[#c97a2b]/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-page-muted mb-1.5" htmlFor="contact-email">
                          Email Address <span className="text-[#c97a2b]">*</span>
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@company.com"
                          className="w-full px-4 py-3 border border-black/[0.1] bg-page rounded-lg text-sm text-page-ink placeholder-page-faint focus:outline-none focus:border-[#c97a2b]/50 focus:ring-1 focus:ring-[#c97a2b]/20 transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-page-muted mb-1.5" htmlFor="contact-company">
                          Company Name
                        </label>
                        <input
                          id="contact-company"
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Acme Corporation"
                          className="w-full px-4 py-3 border border-black/[0.1] bg-page rounded-lg text-sm text-page-ink placeholder-page-faint focus:outline-none focus:border-[#c97a2b]/50 focus:ring-1 focus:ring-[#c97a2b]/20 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-page-muted mb-1.5" htmlFor="contact-phone">
                          Phone Number
                        </label>
                        <input
                          id="contact-phone"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+234 803 301 9908"
                          className="w-full px-4 py-3 border border-black/[0.1] bg-page rounded-lg text-sm text-page-ink placeholder-page-faint focus:outline-none focus:border-[#c97a2b]/50 focus:ring-1 focus:ring-[#c97a2b]/20 transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-page-muted mb-1.5" htmlFor="contact-service">
                        Service of Interest
                      </label>
                      <select
                        id="contact-service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-black/[0.1] bg-page rounded-lg text-sm text-page-ink focus:outline-none focus:border-[#c97a2b]/50 focus:ring-1 focus:ring-[#c97a2b]/20 transition-all cursor-pointer"
                      >
                        <option value="" className="bg-white">Select a service...</option>
                        {services.map((s) => (
                          <option key={s} value={s} className="bg-white">{s}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-page-muted mb-1.5" htmlFor="contact-message">
                        Message <span className="text-[#c97a2b]">*</span>
                        <span className="text-page-faint font-normal ml-1">
                          ({formData.message.length}/500)
                        </span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Tell us about your IT challenges and what you're looking to achieve..."
                        className="w-full px-4 py-3 border border-black/[0.1] bg-page rounded-lg text-sm text-page-ink placeholder-page-faint focus:outline-none focus:border-[#c97a2b]/50 focus:ring-1 focus:ring-[#c97a2b]/20 transition-all resize-none"
                      />
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-line text-red-400 text-sm" />
                        </div>
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3.5 bg-[#c97a2b] text-white font-bold rounded-lg hover:bg-[#b06a22] transition-colors duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap text-sm"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>

                    <p className="text-page-faint text-xs text-center">
                      By submitting, you agree to our Privacy Policy. We'll never share your information.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
